import {
  el,
  getSettings,
  revokeResult,
  state,
} from "./settings.js"
import { formatBytes } from "./state.js"
import { getFramePlan, refreshMetrics } from "./output-plan.js"
import {
  LOOP_DELAY_SLIDER_MAX,
  loopDelayRawToMs,
} from "./loop-delay-scale.js"

const handledUrls = new Set()
let patchedUrl = null
let processingUrl = ""

function readUint16LE(bytes, offset) {
  return bytes[offset] | bytes[offset + 1] << 8
}

function writeUint16LE(bytes, offset, value) {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = value >>> 8 & 0xff
}

function readUint24LE(bytes, offset) {
  return bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16
}

function writeUint24LE(bytes, offset, value) {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = value >>> 8 & 0xff
  bytes[offset + 2] = value >>> 16 & 0xff
}

function readUint32LE(bytes, offset) {
  return (bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16 | bytes[offset + 3] << 24) >>> 0
}

function skipSubBlocks(bytes, offset) {
  while (offset < bytes.length) {
    const size = bytes[offset]
    offset += 1
    if (!size) return offset
    offset += size
  }
  return offset
}

function patchGifLoopDelay(input, delayMs) {
  const bytes = new Uint8Array(input)
  const header = String.fromCharCode(...bytes.slice(0, 6))
  if (header !== "GIF89a" && header !== "GIF87a") throw new Error("Generated GIF header is invalid.")
  let offset = 13
  if (bytes[10] & 0x80) offset += 3 * (1 << ((bytes[10] & 0x07) + 1))
  let lastDelayOffset = -1
  while (offset < bytes.length) {
    const marker = bytes[offset]
    if (marker === 0x3b) break
    if (marker === 0x21) {
      const label = bytes[offset + 1]
      if (label === 0xf9 && bytes[offset + 2] === 4) {
        lastDelayOffset = offset + 4
        offset += 8
      } else {
        offset = skipSubBlocks(bytes, offset + 2)
      }
      continue
    }
    if (marker === 0x2c) {
      const packed = bytes[offset + 9]
      offset += 10
      if (packed & 0x80) offset += 3 * (1 << ((packed & 0x07) + 1))
      offset += 1
      offset = skipSubBlocks(bytes, offset)
      continue
    }
    throw new Error("Generated GIF structure is invalid.")
  }
  if (lastDelayOffset < 0) throw new Error("Generated GIF has no frame delay metadata.")
  const centiseconds = Math.round(delayMs / 10)
  writeUint16LE(bytes, lastDelayOffset, Math.min(0xffff, readUint16LE(bytes, lastDelayOffset) + centiseconds))
  return bytes
}

function ascii(bytes, offset, length = 4) {
  return String.fromCharCode(...bytes.slice(offset, offset + length))
}

function patchWebpLoopDelay(input, delayMs) {
  const bytes = new Uint8Array(input)
  if (ascii(bytes, 0) !== "RIFF" || ascii(bytes, 8) !== "WEBP") throw new Error("Generated WebP header is invalid.")
  let offset = 12
  let lastDurationOffset = -1
  while (offset + 8 <= bytes.length) {
    const tag = ascii(bytes, offset)
    const size = readUint32LE(bytes, offset + 4)
    if (tag === "ANMF" && size >= 16) lastDurationOffset = offset + 20
    offset += 8 + size + (size & 1)
  }
  if (lastDurationOffset < 0) throw new Error("Generated WebP contains no animation frames.")
  writeUint24LE(bytes, lastDurationOffset, Math.min(0xffffff, readUint24LE(bytes, lastDurationOffset) + delayMs))
  return bytes
}

function updateControl() {
  if (!el.loopDelaySlider || !el.loopDelayValue) return
  el.loopDelaySlider.min = "0"
  el.loopDelaySlider.max = String(LOOP_DELAY_SLIDER_MAX)
  el.loopDelaySlider.step = "1"
  el.loopDelayValue.textContent = `${loopDelayRawToMs(el.loopDelaySlider.value)} ms`
}

function clearPatchedUrl() {
  if (patchedUrl) URL.revokeObjectURL(patchedUrl)
  patchedUrl = null
}

function handleDelayChange() {
  updateControl()
  clearPatchedUrl()
  if (state.resultUrl) revokeResult(true)
  refreshMetrics()
  dispatchEvent(new CustomEvent("converter-plan-change", { detail: { reason: "loopDelaySlider" } }))
}

async function processResultUrl(url) {
  if (!url.startsWith("blob:") || handledUrls.has(url) || processingUrl === url) return
  processingUrl = url
  try {
    const response = await fetch(url)
    const source = await response.arrayBuffer()
    const delayMs = getSettings().loopDelayMs
    let blob
    if (delayMs > 0) {
      const bytes = state.format === "webp"
        ? patchWebpLoopDelay(source, delayMs)
        : patchGifLoopDelay(source, delayMs)
      blob = new Blob([bytes], { type: state.format === "webp" ? "image/webp" : "image/gif" })
      clearPatchedUrl()
      patchedUrl = URL.createObjectURL(blob)
      handledUrls.add(patchedUrl)
      el.resultPreview.src = patchedUrl
      el.downloadButton.href = patchedUrl
    } else {
      blob = new Blob([source], { type: state.format === "webp" ? "image/webp" : "image/gif" })
    }
    handledUrls.add(url)
    if (el.statActualSize) {
      el.statActualSize.textContent = formatBytes(blob.size)
      el.statActualSize.title = `${blob.size.toLocaleString()} bytes`
    }
    const plan = getFramePlan()
    el.statusText.textContent = `Done: ${plan.totalFrames} frames · ${formatBytes(blob.size)} actual${delayMs ? ` · ${delayMs} ms loop hold` : ""}.`
  } finally {
    processingUrl = ""
  }
}

function inspectResult() {
  const url = el.resultPreview?.src || ""
  void processResultUrl(url).catch((error) => {
    el.errorBox.textContent = error instanceof Error ? error.message : String(error)
    el.errorBox.hidden = false
  })
}

let initialized = false

export function setupLoopDelayUi() {
  if (initialized) return
  initialized = true
  updateControl()
  el.loopDelaySlider?.addEventListener("input", handleDelayChange)
  el.loopDelaySlider?.addEventListener("change", handleDelayChange)
  if (el.resultPreview) {
    new MutationObserver(inspectResult).observe(el.resultPreview, { attributes: true, attributeFilter: ["src"] })
  }
  addEventListener("beforeunload", clearPatchedUrl, { once: true })
}
